import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

import { ModalTypes, useModalManager } from '@wwt/core/modals'
import {
	getPersistNps,
	setPersistNps
} from '@wwt/shared/utils/storage/adapters/persistNps.ts'

import { NpsAction } from './types/NpsAction.ts'
import { wwtNpsLog } from './wwtNpsLog.ts'

/**
 * Hook `useNps` manages the opening and closing of the NPS modal
 *
 * Usage:
 * - Opens the NPS modal if the user navigates from `/comparison` to `/` and
 * `isNpsAllow` is set to `true`
 * - Closes the NPS modal and clears the state in local storage after user
 * close modal
 *
 * @author Ivan Shchedrovskyi, Nikita Afanasiev
 */
export const useNps = () => {
	const location = useLocation()
	const prevLocation = useRef(location)
	const showModal = useModalManager.use.showModal()
	const closeModal = useModalManager.use.closeModal()

	const modalId = useRef<null | string>(null)

	const getNpsAction = async (): Promise<NpsAction> => {
		const persist = await getPersistNps()

		if (persist === null || persist === undefined) {
			return NpsAction.open
		}

		return NpsAction.close
	}

	const handleNpsAction = (action: NpsAction) => {
		if (action === NpsAction.open) {
			wwtNpsLog('Open nps modal')
			const showedModalId = showModal(ModalTypes.Nps, {
				// eslint-disable-next-line no-use-before-define
				onClose: onNpsClose,
				modalContentProps: {
					h: { xl: '48.5rem', md: '43.9375rem', base: '48.1875rem' }
				}
			})

			if (showedModalId === null) {
				throw new Error('Error on `showModal` NPS')
			}

			modalId.current = showedModalId
			return
		}

		if (action === NpsAction.close) {
			wwtNpsLog('Close nps modal')

			if (modalId.current === null) {
				throw new Error('We cannot close NPS modal if it not opened')
			}

			closeModal(modalId.current)
		}
	}

	useEffect(() => {
		if (prevLocation.current.pathname === location.pathname) {
			prevLocation.current.pathname = location.pathname
			return
		}

		wwtNpsLog(
			prevLocation.current.pathname,
			location.pathname,
			location.state?.isNpsAllow
		)

		if (
			prevLocation.current.pathname === '/comparison' &&
			location.pathname === '/' &&
			(location.state?.isNpsAllow === undefined ||
				location.state?.isNpsAllow === true)
		) {
			wwtNpsLog('Try to show nps modal')
			getNpsAction().then(handleNpsAction).catch(console.error)
		}

		location.state = { ...(location.state ?? {}), isNpsAllow: true }
		prevLocation.current.pathname = location.pathname
	}, [location])

	const onNpsClose = () => {
		wwtNpsLog('Set null to nps store state')
		getPersistNps().then(persist =>
			persist ? null : setPersistNps(null).catch(console.error)
		)

		handleNpsAction(NpsAction.close)
	}
}
